/**
 * iCRAFTZ LibraryPage · v3 rebuild — 2026-02-05
 *
 * Focused slice per Founder brief:
 *   (a) Category filter chips (real DATA categories) with live counts
 *   (d) NO left filter panel, NO funnel toggle icon
 *   (e) Restyle to HomeV3 aesthetic — shared Navigation, Footer, GoldRule
 *
 * NOT wired in this slice:
 *   - Search bar (task b) — deferred
 *   - Sort dropdown + grid/list toggle (task c) — deferred
 *   - Sanskrit sub-labels — Founder said no
 *   - Backend changes — Founder said no
 *
 * Book data source: /app/frontend/src/data/bookExamples.js (59 books)
 * Detail page: existing /book/:id (BookDetailPage.jsx) — untouched
 */

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import GoldRule from "../components/homev3/GoldRule";
import ParchmentBackground from "../components/ParchmentBackground";
import { T, R, EMBLEM } from "../components/homev3/tokens";
import { bookExamples } from "../data/bookExamples";

// ── Category taxonomy (DATA categories, not painted-PNG list) ─────────────
// Order matches the natural life-arc used across the app.
const CATEGORIES = [
  "Habits",
  "Mindset",
  "Wealth",
  "Leadership",
  "Purpose",
  "Strategy",
  "Deep Focus",
  "Stillness",
  "Vitality",
];

// A book matches a category if EITHER `category` (single) OR any entry
// of `categories` (multi-tag) equals the chip label.
function bookMatches(book, cat) {
  if (cat === "All") return true;
  if (book.category === cat) return true;
  if (Array.isArray(book.categories) && book.categories.includes(cat)) return true;
  return false;
}

export default function LibraryPage() {
  const [active, setActive] = useState("All");

  // Live per-category counts — recomputed once from the static array.
  const counts = useMemo(() => {
    const c = { All: bookExamples.length };
    CATEGORIES.forEach((cat) => {
      c[cat] = bookExamples.filter((b) => bookMatches(b, cat)).length;
    });
    return c;
  }, []);

  const filtered = useMemo(
    () => bookExamples.filter((b) => bookMatches(b, active)),
    [active]
  );

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "transparent" }}
      data-testid="library-v3-root"
    >
      <ParchmentBackground />
      <Navigation activePath={R.library} />

      {/* Top emblem — matches HomeV3 placement.
          Enlarged from 130 to 180 (mobile-slice-5) so it reads with the
          same visual weight as the homepage card emblem. z-index:60 keeps
          it above the fixed nav (z:50), straddling the nav boundary.
          Hidden below 900px (see inline <style> below) because at narrow
          viewports it centers over the "LIBRARY — Wisdom from N Books"
          heading — pre-existing overlap surfaced during the parchment
          rollout mobile audit. */}
      <img
        src={EMBLEM}
        alt="iCRAFTZ emblem"
        className="absolute pointer-events-none library-hero-emblem"
        style={{ width: 180, height: "auto", top: 8, right: 47, zIndex: 60 }}
      />

      <style>{`
        @media (max-width: 899px) {
          .library-hero-emblem { display: none !important; }
        }
      `}</style>

      {/* ─── Section header ────────────────────────────────────────── */}
      <header
        className="w-full flex flex-col items-center"
        style={{ paddingTop: 96, paddingBottom: 20, background: "transparent" }}
        data-testid="library-v3-header"
      >
        <div className="flex items-center justify-center gap-4 mb-3" style={{ width: "min(720px, 92%)" }}>
          <GoldRule />
          <p
            className="m-0 text-center"
            style={{ fontFamily: T.F_HEADING, fontSize: 13, letterSpacing: "0.20em", color: T.DARK_BROWN, whiteSpace: "nowrap" }}
          >
            LIBRARY &ndash; Wisdom from {bookExamples.length} Books
          </p>
          <GoldRule />
        </div>
        <p
          className="m-0"
          style={{ fontFamily: T.F_BODY, fontStyle: "italic", fontSize: 15, color: T.MUTED_BROWN, lineHeight: 1.55, textAlign: "center", maxWidth: 620 }}
        >
          Curated non-fiction wisdom &mdash; extracted, condensed, and translated into action.
          Choose a category or explore all.
        </p>
      </header>

      {/* ─── Category chips (task a) ───────────────────────────────── */}
      <div
        className="flex flex-wrap items-center justify-center gap-2 px-6"
        style={{ paddingBottom: 24, background: T.PARCHMENT_HI }}
        data-testid="library-v3-chips"
      >
        {["All", ...CATEGORIES].map((cat) => {
          const isActive = active === cat;
          const n = counts[cat] || 0;
          return (
            <button
              key={cat}
              type="button"
              data-testid={`library-v3-chip-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              data-active={isActive}
              onClick={() => setActive(cat)}
              className="rounded-full transition-all duration-150"
              style={{
                fontFamily: T.F_HEADING,
                fontSize: 12,
                letterSpacing: "0.06em",
                padding: "8px 18px",
                border: `1px solid ${T.GOLD}`,
                background: isActive ? T.DARK_BROWN : "rgba(240,230,204,0.65)",
                color:      isActive ? T.PARCHMENT_LT : T.DARK_BROWN,
                cursor:     "pointer",
                boxShadow:  isActive ? "0 3px 8px rgba(44,24,16,0.24)" : "none",
              }}
            >
              {cat}
              <span
                className="ml-2"
                style={{
                  fontFamily: T.F_BODY,
                  fontStyle: "italic",
                  fontSize: 11,
                  opacity: 0.8,
                }}
                data-testid={`library-v3-chip-count-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {/* ─── Book grid ─────────────────────────────────────────────── */}
      <main
        className="flex-1 w-full"
        style={{ background: "transparent", padding: "8px 48px 72px" }}
      >
        <div
          className="grid gap-6"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            gridTemplateColumns: "repeat(auto-fill, minmax(196px, 1fr))",
          }}
          data-testid="library-v3-grid"
        >
          {filtered.map((book) => (
            <Link
              key={book.id}
              to={`/book/${book.id}`}
              data-testid={`library-v3-book-${book.id}`}
              className="no-underline flex flex-col rounded-lg overflow-hidden group"
              style={{
                background: T.PARCHMENT_LT,
                border: `1px solid ${T.GOLD}`,
                boxShadow: "0 3px 10px rgba(44,24,16,0.12)",
                transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 10px 22px rgba(44,24,16,0.24)";
                e.currentTarget.style.borderColor = "#B8942E";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 3px 10px rgba(44,24,16,0.12)";
                e.currentTarget.style.borderColor = T.GOLD;
              }}
            >
              <div style={{ aspectRatio: "3/4", background: "#EADFC4", overflow: "hidden" }}>
                <img
                  src={book.coverUrl || book.image}
                  alt={book.title}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='120'%20height='160'%3E%3Crect%20width='120'%20height='160'%20fill='%23EADFC4'/%3E%3Crect%20x='34'%20y='40'%20width='52'%20height='80'%20fill='none'%20stroke='%23a37e2a'%20stroke-width='2'/%3E%3Cline%20x1='60'%20y1='40'%20x2='60'%20y2='120'%20stroke='%23a37e2a'%20stroke-width='2'/%3E%3C/svg%3E"; }}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
              <div style={{ padding: "12px 14px 14px", background: T.PARCHMENT_LT }}>
                <p
                  className="m-0 mb-1"
                  style={{
                    fontFamily: T.F_HEADING,
                    fontSize: 13,
                    fontWeight: 600,
                    color: T.DARK_BROWN,
                    lineHeight: 1.25,
                    letterSpacing: "0.01em",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {book.title}
                </p>
                <p
                  className="m-0"
                  style={{
                    fontFamily: T.F_BODY,
                    fontSize: 13,
                    fontStyle: "italic",
                    color: T.MUTED_BROWN,
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {book.author}
                </p>
              </div>
            </Link>
          ))}

          {filtered.length === 0 && (
            <p
              className="col-span-full text-center"
              style={{ fontFamily: T.F_BODY, fontStyle: "italic", color: T.MUTED_BROWN, padding: "48px 0" }}
              data-testid="library-v3-empty"
            >
              No books in this category yet.
            </p>
          )}
        </div>
      </main>

      <Footer activePath={R.library} />
    </div>
  );
}
